import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { newsAPI, type News as NewsArticle, type NewsCategory } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  Plus,
  Newspaper,
  Pin,
  Star,
  Eye,
  ExternalLink,
  AlertCircle,
  Loader2,
  Bell,
  CheckCheck,
  FileText,
  Edit,
  Trash2,
  Clock,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { canManageNews } from '../lib/newsAccess';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import { useIsCustomer } from '../hooks/usePortalMode';
import { toast } from 'sonner';

const PLACEHOLDER_ICONS = ['📢', '📰', '💡', '🔔', '📋', '🗞️', '✨', '📣', '🏭', '⚙️'];

function getStableIcon(title: string): string {
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = ((hash << 5) - hash) + title.charCodeAt(i);
    hash |= 0;
  }
  return PLACEHOLDER_ICONS[Math.abs(hash) % PLACEHOLDER_ICONS.length];
}

const PASTEL_GRADIENTS = [
  'from-blue-50 to-blue-100 dark:from-slate-800 dark:to-slate-700',
  'from-amber-50 to-yellow-100 dark:from-slate-800 dark:to-slate-700',
  'from-emerald-50 to-green-100 dark:from-slate-800 dark:to-slate-700',
  'from-pink-50 to-rose-100 dark:from-slate-800 dark:to-slate-700',
  'from-violet-50 to-indigo-100 dark:from-slate-800 dark:to-slate-700',
  'from-sky-50 to-cyan-100 dark:from-slate-800 dark:to-slate-700',
];

function NoImagePlaceholder({ title, isFeatured, isUnread }: {
  title: string;
  isFeatured?: boolean;
  isUnread?: boolean;
}) {
  const icon = getStableIcon(title);
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = ((hash << 5) - hash) + title.charCodeAt(i);
    hash |= 0;
  }
  const gradient = PASTEL_GRADIENTS[Math.abs(hash) % PASTEL_GRADIENTS.length];

  return (
    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center ${
      isFeatured ? 'w-full h-64 mb-4' : 'w-44 h-28 shrink-0'
    }`}>
      <span className={`select-none ${isFeatured ? 'text-6xl' : 'text-3xl'}`} role="img" aria-label="article icon">
        {icon}
      </span>
      {isUnread && (
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white shadow-sm">
            <Bell className="h-3 w-3" />
            New
          </span>
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article, isAdmin, isFeatured, onDelete, onClick, onEdit }: {
  article: NewsArticle;
  isAdmin: boolean;
  isFeatured?: boolean;
  onDelete: (e: React.MouseEvent, article: NewsArticle) => void;
  onClick: (article: NewsArticle) => void;
  onEdit: (e: React.MouseEvent, article: NewsArticle) => void;
}) {
  const publishedDate = article.published_at || article.created_at;
  const timeAgo = formatDistanceToNow(new Date(publishedDate), { addSuffix: true });
  const formattedDate = format(new Date(publishedDate), 'MMM d, yyyy');
  const readTime = Math.max(1, Math.ceil((article.content?.length || 0) / 1000));

  return (
    <article
      className={`group cursor-pointer ${isFeatured ? '' : 'flex gap-5'}`}
      onClick={() => onClick(article)}
    >
      {/* Image */}
      {article.image_url ? (
        <div className={`relative overflow-hidden rounded-xl ${
          isFeatured ? 'w-full h-64 mb-4' : 'w-44 h-28 shrink-0'
        }`}>
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {article.is_unread && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white shadow-sm">
                <Bell className="h-3 w-3" />
                New
              </span>
            </div>
          )}
        </div>
      ) : (
        <NoImagePlaceholder
          title={article.title}
          isFeatured={isFeatured}
          isUnread={article.is_unread}
        />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Tags row */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {article.category_name && (
            <span
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: article.category_color || '#6366f1' }}
            >
              {article.category_name}
            </span>
          )}
          {article.is_pinned && (
            <Badge variant="secondary" className="bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0 text-[10px] px-1.5 py-0">
              <Pin className="h-2.5 w-2.5 mr-0.5" />
              Pinned
            </Badge>
          )}
          {article.is_featured && (
            <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-0 text-[10px] px-1.5 py-0">
              <Star className="h-2.5 w-2.5 mr-0.5" />
              Featured
            </Badge>
          )}
          {article.priority !== 'normal' && article.is_published && (
            <Badge variant="secondary" className={
              article.priority === 'urgent'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-0 text-[10px] px-1.5 py-0'
                : article.priority === 'high'
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-0 text-[10px] px-1.5 py-0'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-0 text-[10px] px-1.5 py-0'
            }>
              {article.priority.charAt(0).toUpperCase() + article.priority.slice(1)}
            </Badge>
          )}
          {isAdmin && !article.is_published && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-dashed">
              <FileText className="h-2.5 w-2.5 mr-0.5" />
              Draft
            </Badge>
          )}
        </div>

        {/* Title */}
        <h3 className={`font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 ${
          isFeatured ? 'text-2xl leading-tight mb-2' : 'text-base leading-snug mb-1'
        }`}>
          {article.title}
        </h3>

        {/* Summary */}
        <p className={`text-muted-foreground leading-relaxed ${
          isFeatured ? 'text-base line-clamp-3 mb-3' : 'text-sm line-clamp-2 mb-2'
        }`}>
          {article.summary}
        </p>

        {/* Meta row */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">{article.author_display}</span>
          <span className="text-muted-foreground/50">·</span>
          <span title={formattedDate}>{timeAgo}</span>
          <span className="text-muted-foreground/50">·</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {readTime} min read
          </span>
          {article.view_count > 0 && (
            <>
              <span className="text-muted-foreground/50">·</span>
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {article.view_count}
              </span>
            </>
          )}
          {article.external_link && (
            <>
              <span className="text-muted-foreground/50">·</span>
              <ExternalLink className="h-3 w-3" />
            </>
          )}
          {/* Admin actions */}
          {isAdmin && !article.is_published && (
            <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={(e) => onEdit(e, article)}>
                <Edit className="h-3 w-3 mr-1" />
                Edit
              </Button>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-destructive hover:text-destructive" onClick={(e) => onDelete(e, article)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export default function News() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const isCustomer = useIsCustomer();
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const isAdmin = isCustomer && canManageNews(user);

  useEffect(() => {
    loadNews();
    loadCategories();
    loadUnreadCount();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getNews();
      setArticles(response.data.results || []);
      setError(null);
    } catch (err: unknown) {
      console.error('Error loading news:', err);
      setError('Failed to load news articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await newsAPI.getCategories();
      setCategories(response.data.results || []);
    } catch (err: unknown) {
      console.error('Error loading categories:', err);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await newsAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (err: unknown) {
      console.error('Error loading unread count:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await newsAPI.markAllRead();
      setUnreadCount(0);
      setArticles(articles.map(a => ({ ...a, is_unread: false })));
    } catch (err: unknown) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleArticleClick = (article: NewsArticle) => {
    navigate(`/news/${article.id}`);
  };

  const handleEdit = (e: React.MouseEvent, article: NewsArticle) => {
    e.stopPropagation();
    navigate(`/news/edit/${article.id}`);
  };

  const handleDeleteDraft = async (e: React.MouseEvent, article: NewsArticle) => {
    e.stopPropagation();
    const ok = await confirm({ title: 'Delete Draft', description: `Delete draft "${article.title}"?`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await newsAPI.deleteNews(article.id);
      setArticles(articles.filter((a) => a.id !== article.id));
      toast.success('Draft deleted');
    } catch (err: unknown) {
      console.error('Error deleting draft:', err);
      toast.error('Failed to delete draft');
    }
  };

  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.summary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === 'all' || article.category?.toString() === categoryFilter;
    const matchesPriority =
      priorityFilter === 'all' || article.priority === priorityFilter;
    return matchesSearch && matchesCategory && matchesPriority;
  });

  // Split: featured/pinned article goes hero, rest in grid
  const featuredArticle = filteredArticles.find(a => a.is_featured || a.is_pinned);
  const remainingArticles = featuredArticle
    ? filteredArticles.filter(a => a.id !== featuredArticle.id)
    : filteredArticles;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading news articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">News & Announcements</h1>
          <p className="text-muted-foreground mt-1">
            Stay updated with the latest news and announcements
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read ({unreadCount})
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={() => navigate('/news/create')}>
              <Plus className="h-4 w-4 mr-2" />
              Create
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search & Filters — clean inline bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-muted/40 border-0 focus-visible:bg-background"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-44 bg-muted/40 border-0">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id.toString()}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40 bg-muted/40 border-0">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Empty State */}
      {filteredArticles.length === 0 ? (
        <div className="text-center py-20">
          <Newspaper className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-1">No articles found</h3>
          <p className="text-muted-foreground text-sm">
            {searchTerm || categoryFilter !== 'all' || priorityFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Check back later for updates'}
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {/* Hero: Featured / Pinned Article */}
          {featuredArticle && (
            <div className="border-b pb-10">
              <ArticleCard
                article={featuredArticle}
                isAdmin={isAdmin}
                isFeatured
                onDelete={handleDeleteDraft}
                onClick={handleArticleClick}
                onEdit={handleEdit}
              />
            </div>
          )}

          {/* Article List */}
          <div className="divide-y">
            {remainingArticles.map((article) => (
              <div key={article.id} className="py-6 first:pt-0 last:pb-0">
                <ArticleCard
                  article={article}
                  isAdmin={isAdmin}
                  onDelete={handleDeleteDraft}
                  onClick={handleArticleClick}
                  onEdit={handleEdit}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
